import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useLanguage } from "@/hooks/use-language"
import { useNavigate } from "react-router-dom"
import {
  Lock,
  Crown,
  XCircle,
  ArrowRight
} from "lucide-react"

interface TrialExpiredBlockProps {
  onUpgrade?: () => void
}

export function TrialExpiredBlock({ onUpgrade }: TrialExpiredBlockProps) {
  const { language } = useLanguage()
  const navigate = useNavigate()

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8">
      <Card className="border-destructive/50 bg-destructive/5 shadow-2xl shadow-destructive/20 max-w-2xl w-full">
        <CardContent className="p-12 text-center space-y-8">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-24 h-24 rounded-full bg-destructive/10 flex items-center justify-center border-4 border-destructive/30 shadow-lg">
              <Lock className="w-12 h-12 text-destructive" />
            </div>
          </div>

          {/* Badge */}
          <div className="flex justify-center">
            <Badge variant="outline" className="border-destructive/30 text-destructive bg-destructive/10 text-base px-4 py-2">
              {language === "en" ? "Free Trial Expired" : "Prueba Gratuita Expirada"}
            </Badge>
          </div>

          {/* Title */}
          <div className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">
              {language === "en" ? "Your Free Trial Has Ended" : "Tu Prueba Gratuita Ha Finalizado"}
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              {language === "en"
                ? "Your 7-day free trial has expired. Upgrade to Premium to continue receiving signals and unlock all features."
                : "Tu prueba gratuita de 7 días ha expirado. Actualiza a Premium para continuar recibiendo señales y desbloquear todas las funciones."}
            </p>
          </div>

          {/* Features Blocked */}
          <div className="bg-background/50 border border-border/50 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {language === "en" ? "What's Blocked:" : "Qué está bloqueado:"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {language === "en" ? "Signal Access" : "Acceso a Señales"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === "en" ? "Dashboard signals blocked" : "Señales del dashboard bloqueadas"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {language === "en" ? "Telegram Channel" : "Canal de Telegram"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === "en" ? "Public channel access blocked" : "Acceso al canal público bloqueado"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {language === "en" ? "Signal History" : "Historial de Señales"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === "en" ? "Limited access to history" : "Acceso limitado al historial"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {language === "en" ? "Premium Features" : "Funciones Premium"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === "en" ? "All premium features locked" : "Todas las funciones premium bloqueadas"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button
              onClick={() => {
                if (onUpgrade) {
                  onUpgrade()
                } else {
                  navigate("/payment")
                }
              }}
              size="lg"
              className="gap-2 min-w-[240px]"
            >
              <Crown className="w-5 h-5" />
              {language === "en" ? "Upgrade to Premium" : "Adquirir Servicio Premium"}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button
              onClick={() => navigate("/settings")}
              variant="outline"
              size="lg"
              className="gap-2 min-w-[240px] border-primary/50 text-primary hover:bg-primary/10"
            >
              {language === "en" ? "Manage License" : "Gestionar Licencia"}
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-sm text-muted-foreground pt-4">
            {language === "en"
              ? "Need help? Contact support or check your license status in Settings."
              : "¿Necesitas ayuda? Contacta soporte o revisa el estado de tu licencia en Configuración."}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

